package com.arena_web.ui.sections.header

import androidx.compose.runtime.*
import com.arena_web.data.dto.UserPostDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.components.overlay.BuyCoinsDialog
import com.arena_web.ui.components.widgets.HiddenScrollbarStyle
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.mobile.openUserProfile
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.OAuthHelper
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

// =============================================================================
// Mobile header bottom sheets — the phone counterparts of the desktop header
// dropdowns (AppHeader's ProfilePill / NotificationDropdown / ActivityDropdown).
// Each sheet reuses the desktop panel content 1:1 (ProfileMenuItems /
// NotificationPanelItems / ActivityPanelItems) inside a [BottomSheet], whose
// `--scale: var(--mobile-scale)` makes the desktop `s()` styling render
// against the 390-px mobile baseline.
// =============================================================================

/**
 * Profile sheet — opened by tapping the avatar in [MobileTopBar]. Same menu as
 * the desktop profile dropdown: avatar upload header, Visual Style palette,
 * wallet, sign out and delete profile (both behind a confirm dialog).
 */
@Composable
fun MobileProfileSheet(onDismiss: () -> Unit) {
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val scope = rememberCoroutineScope()

    var userName by remember {
        mutableStateOf(
            Storage.get(StorageKeys.FULL_NAME)
                ?: Storage.get(StorageKeys.USERNAME)
                ?: ""
        )
    }
    var userHandle by remember { mutableStateOf(Storage.get(StorageKeys.USERNAME) ?: "") }
    var avatarUrl by remember { mutableStateOf(Storage.get(StorageKeys.AVATAR_URL)) }

    // The avatar upload lives inside this sheet — refresh the header row when
    // it (or a name edit elsewhere) lands.
    DisposableEffect(Unit) {
        val avatarListener: (org.w3c.dom.events.Event) -> Unit = {
            avatarUrl = Storage.get(StorageKeys.AVATAR_URL)
        }
        val nameListener: (org.w3c.dom.events.Event) -> Unit = {
            userName = Storage.get(StorageKeys.FULL_NAME)
                ?: Storage.get(StorageKeys.USERNAME)
                ?: ""
            userHandle = Storage.get(StorageKeys.USERNAME) ?: ""
        }
        kotlinx.browser.window.addEventListener("avatar-updated", avatarListener)
        kotlinx.browser.window.addEventListener("name-updated", nameListener)
        onDispose {
            kotlinx.browser.window.removeEventListener("avatar-updated", avatarListener)
            kotlinx.browser.window.removeEventListener("name-updated", nameListener)
        }
    }

    val walletBalance by container.walletBalance.collectAsState()
    LaunchedEffect(Unit) { container.ensureWalletLoaded() }

    val selectedSportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME) ?: "All"

    var isLoggingOut by remember { mutableStateOf(false) }
    var isDeletingAccount by remember { mutableStateOf(false) }
    var showSignOutConfirm by remember { mutableStateOf(false) }
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var buyCoinsOpen by remember { mutableStateOf(false) }

    BottomSheet(onDismiss = onDismiss) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .minHeight(0.px)
                .sGap(22)
                .sPaddingTop(12)
                .sPaddingLeft(4).sPaddingRight(4)
                .styleModifier { property("overflow-y", "auto") }
                .then(HiddenScrollbarStyle.toModifier()),
            horizontalAlignment = Alignment.Start,
        ) {
            ProfileMenuItems(
                name = userName.ifEmpty { t.headerUserFallbackName },
                username = userHandle,
                avatarUrl = avatarUrl,
                walletBalance = walletBalance,
                selectedSportName = selectedSportName,
                isLoggingOut = isLoggingOut,
                onOpenWallet = { buyCoinsOpen = true },
                onSignOut = { showSignOutConfirm = true },
                onDelete = { showDeleteConfirm = true },
            )
        }
    }

    // Dialogs are SIBLINGS of the sheet (the panel's translateY transform would
    // otherwise become the containing block for their position:fixed) and sit
    // above its z-index 1100. The wrapper re-applies the mobile scale that the
    // sheet panel normally provides.
    if (showSignOutConfirm || showDeleteConfirm) {
        Div(attrs = Modifier.styleModifier { property("--scale", "var(--mobile-scale)") }.toAttrs()) {
            if (showSignOutConfirm) {
                ConfirmActionDialog(
                    icon = AppIcons.Exit,
                    title = t.headerConfirmTitle,
                    message = t.headerSignOutConfirmMessage,
                    confirmLabel = t.headerSignOutConfirmLabel,
                    confirmBgColor = SportColors.Football.primary,
                    confirmHorizontalPadding = 48,
                    isConfirming = isLoggingOut,
                    zIndex = 1300,
                    onConfirm = {
                        if (!isLoggingOut) {
                            isLoggingOut = true
                            scope.launch {
                                val logoutUseCase = container.getLogoutUseCase()
                                logoutUseCase()
                                container.clearAuthenticatedClients()
                                OAuthHelper.resetGoogleSession()
                                kotlinx.browser.window.location.reload()
                            }
                        }
                    },
                    onCancel = { showSignOutConfirm = false },
                )
            }
            if (showDeleteConfirm) {
                ConfirmActionDialog(
                    icon = AppIcons.Trash,
                    title = t.headerConfirmTitle,
                    message = t.headerDeleteConfirmMessage,
                    confirmLabel = t.headerDeleteConfirmLabel,
                    confirmBgColor = Color.rgb(255, 66, 66),
                    isConfirming = isDeletingAccount,
                    zIndex = 1300,
                    onConfirm = {
                        if (!isDeletingAccount) {
                            isDeletingAccount = true
                            scope.launch {
                                val result = container.getDeleteAccountUseCase().invoke()
                                if (result.isFailure) {
                                    isDeletingAccount = false
                                    kotlinx.browser.window.alert(
                                        t.headerDeleteProfileFailed
                                    )
                                    return@launch
                                }
                                container.clearAuthenticatedClients()
                                OAuthHelper.resetGoogleSession()
                                kotlinx.browser.window.location.reload()
                            }
                        }
                    },
                    onCancel = { if (!isDeletingAccount) showDeleteConfirm = false },
                )
            }
        }
    }

    // Renders its own mobile BottomSheet; mounted after ours, so it stacks on top.
    BuyCoinsDialog(
        isOpen = buyCoinsOpen,
        onClose = { buyCoinsOpen = false },
        walletBalance = walletBalance,
    )
}

/**
 * Notifications sheet — opened by tapping the bell in [MobileTopBar]. Same
 * list, mark-read and accept/decline behaviour as the desktop dropdown.
 */
@Composable
fun MobileNotificationsSheet(onDismiss: () -> Unit) {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val scope = rememberCoroutineScope()
    val sportColors = LocalSportColors.current

    val notifications by container.notifications.collectAsState()
    val unreadCount by container.unreadNotificationCount.collectAsState()
    val hasMore by container.hasMoreNotifications.collectAsState()
    val handlers = rememberNotificationHandlers()

    // Post shown in the comments sheet — opened from LIKE_POST / COMMENT_POST.
    var openPost by remember { mutableStateOf<UserPostDto?>(null) }

    // Follow state for NEW_FOLLOWER actors — same lazy per-actor fetch +
    // optimistic toggle as the desktop NotificationButton.
    val followRelations = remember { mutableStateMapOf<String, Boolean>() }

    LaunchedEffect(Unit) { container.ensureNotificationsListLoaded() }

    // Auto-mark loaded unread notifications as read after a 2s dwell; closing
    // the sheet earlier cancels the coroutine before the delay completes.
    LaunchedEffect(Unit) {
        delay(2000)
        container.notifications.value
            .filter { !it.isRead }
            .forEach { handlers.markAsRead(it.id) }
    }

    LaunchedEffect(notifications) {
        val repo = container.followRepository ?: return@LaunchedEffect
        val actorIds = notifications
            .filter { it.type.uppercase() == "NEW_FOLLOWER" }
            .mapNotNull { it.parseDataJson()["actorId"]?.takeIf { id -> id.isNotBlank() } }
            .distinct()
        actorIds.forEach { actorId ->
            if (actorId !in followRelations) {
                repo.getRelation(actorId).onSuccess { rel ->
                    followRelations[actorId] = rel.isFollowing
                }
            }
        }
    }

    BottomSheet(onDismiss = onDismiss, fullHeight = true) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .minHeight(0.px)
                .sGap(12)
                .sPaddingTop(8)
                .styleModifier { property("overflow-y", "auto") }
                .then(HiddenScrollbarStyle.toModifier()),
        ) {
            if (notifications.isEmpty()) {
                SheetEmptyState(icon = AppIcons.Notifications, message = t.headerNoNotifications)
            } else {
                NotificationPanelItems(
                    notifications = notifications,
                    hasMore = hasMore,
                    unreadCount = unreadCount,
                    sportColors = sportColors,
                    followRelations = followRelations,
                    onLoadMore = handlers.loadMore,
                    onMarkAsRead = handlers.markAsRead,
                    onMarkAllAsRead = handlers.markAllAsRead,
                    onAction = handlers.action,
                    onOpenPost = { postId, notificationId ->
                        if (postId.isNotBlank()) {
                            scope.launch {
                                container.userPostRepository?.getPost(postId)?.onSuccess { post ->
                                    openPost = post
                                    handlers.markAsRead(notificationId)
                                }
                            }
                        }
                    },
                    onAvatarClick = { actorId ->
                        if (actorId.isNotBlank()) {
                            onDismiss()
                            openUserProfile(ctx, actorId)
                        }
                    },
                    onFollowToggle = { actorId, notificationId ->
                        val repo = container.followRepository
                        if (repo != null && actorId.isNotBlank()) {
                            val currentlyFollowing = followRelations[actorId] ?: false
                            // Optimistic flip — reverted on failure.
                            followRelations[actorId] = !currentlyFollowing
                            scope.launch {
                                val result = if (currentlyFollowing) repo.unfollowUser(actorId)
                                    else repo.followUser(actorId)
                                result.onSuccess {
                                    handlers.markAsRead(notificationId)
                                }.onFailure {
                                    followRelations[actorId] = currentlyFollowing
                                }
                            }
                        }
                    },
                )
                SheetScrollEndSpacer()
            }
        }
    }

    // Comments sheet for LIKE_POST / COMMENT_POST — stacks over this sheet.
    val postRepo = container.userPostRepository
    val post = openPost
    if (post != null && postRepo != null) {
        com.arena_web.ui.sections.feed.mobile.MobilePostCommentsSheet(
            post = post,
            repository = postRepo,
            onDismiss = { openPost = null },
        )
    }
}

/**
 * Activity sheet — opened by tapping the pulse action in [MobileTopBar] (the
 * Arena page swaps the bell for it). Same "Your Active Events" list as the
 * desktop My Activity dropdown.
 */
@Composable
fun MobileActivitySheet(onDismiss: () -> Unit) {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current

    val activity by container.activityData.collectAsState()
    val activityCount by container.activityCount.collectAsState()

    LaunchedEffect(Unit) { container.ensureActivityLoaded() }

    BottomSheet(onDismiss = onDismiss) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .minHeight(0.px)
                .sGap(18)
                .sPaddingTop(8)
                .styleModifier { property("overflow-y", "auto") }
                .then(HiddenScrollbarStyle.toModifier()),
        ) {
            val current = activity
            when {
                activityCount == 0 -> SheetEmptyState(
                    icon = AppIcons.Pulse,
                    message = t.headerNoActiveEvents,
                )
                current != null -> {
                    ActivityPanelItems(
                        activity = current,
                        sportColors = sportColors,
                        onEventClick = { eventId ->
                            onDismiss()
                            ctx.router.navigateTo(Routes.eventDetails(eventId))
                        },
                    )
                    SheetScrollEndSpacer()
                }
                else -> SheetEmptyState(icon = AppIcons.Pulse, message = t.commonLoading)
            }
        }
    }
}

// Centered icon + message — the sheet-sized take on the desktop EmptyDropdown.
@Composable
private fun SheetEmptyState(icon: String, message: String) {
    Column(
        modifier = Modifier.fillMaxWidth().sPadding(40, 20).sGap(10),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Icon(src = icon, modifier = Modifier.sSize(28))
        SpanText(
            message,
            modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
                .styleModifier { property("text-align", "center") }
        )
    }
}

// Trailing spacer for the sheets' scroll columns — padding-bottom on a flex
// scroll container is dropped at scroll-end on Chrome/Safari.
@Composable
private fun SheetScrollEndSpacer() {
    Div(
        attrs = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("height", s(12))
                property("flex-shrink", "0")
            }
            .toAttrs()
    )
}
